import {
	AuthenticationRequiredResult,
	create,
	EgnyteConnectionListItemModel,
	EgnyteConnectionSaveModel,
	get,
	update
} from "client/Api/Egnyte/egnyteConfiguration";
import { SecretValue } from "client/Api/Apps/common";
import { HttpRequestError } from "client/Api/httpClient";
import {
	CopyControl,
	Dialog,
	Form,
	FormattedText,
	FormItem,
	InputLike,
	InputRow,
	SecretValueInput,
	TextInput,
	Toolbar,
	ValidationError
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import { IValueInputAttachable } from "client/Forms/attachable";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog,
	useStableImperativeCallback,
	useUnmountedCancellation
} from "client/Hooks";
import { isNil } from "client/Misc/utils";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IRootValidation,
	IValidationConfiguration
} from "client/Validation";
import {
	doesNotExceedLength,
	inline,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	createUrlRelativeToEnvironment,
	navigateToUriInPopOutWindow
} from "client_legacy/ndaw/navigator";
import {
	ReactElement,
	useCallback,
	useEffect,
	useState
} from "react";

type EgnyteConnectionDialogProps = {
	connectionId?: number;
	onAfterSave(item: EgnyteConnectionListItemModel): void;
	onClose(): void;
};

const EgnyteConnectionDialog = (props: EgnyteConnectionDialogProps): ReactElement => {
	const {
		connectionId,
		onAfterSave,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);

	const [formData, setFormData] = useState<EgnyteConnectionSaveModel>({
		name: "",
		domainName: "",
		clientId: "",
		clientSecret: {
			containsNewValue: true,
			value: ""
		}
	});

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const formHandler: IFormHandler<EgnyteConnectionSaveModel> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const [dialogContentState, setDialogContentState] = useState<DialogContentState>({
		kind: "form"
	});

	const submitHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);
			const validation: IRootValidation<EgnyteConnectionSaveModel> = formHandler.validation;

			try {
				const validationResult: InputValidationResult = await validation.validate(globalCancellation);
				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setDialogContentState({
						kind: "form"
					});
					setIsBusy(false);
					return;
				}

				let id: number | undefined = connectionId;

				if (id === undefined) {
					id = (await create(formData, globalCancellation))
						.id;
				} else {
					await update(
						id,
						formData,
						globalCancellation
					);
				}

				onAfterSave({
					id,
					name: formData.name,
					domainName: formData.domainName
				});

			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				let wasHandled: boolean = false;

				if (validation.tryProcessServerError(e)) {
					setDialogContentState({
						kind: "form"
					});

					wasHandled = true;
				} else if (e instanceof HttpRequestError && e.statusCode === 409) {
					try {
						const result: AuthenticationRequiredResult = e.readJson<AuthenticationRequiredResult>();
						if (result.redirectUrl) {
							if (dialogContentState.kind === "authentication") {
								alertManager.showError(localize("_FailedToAuthenticatePleaseTryAgain"));
							}

							setDialogContentState({
								kind: "authentication",
								authenticationUrl: result.redirectUrl,
								backHandler: () => setDialogContentState({ kind: "form" })
							});
							wasHandled = true;
						} else {
							console.error("Unexpected result", result);
						}

					} catch (e) {
						console.error(e);
					}
				}

				if (!wasHandled) {
					console.error(e);
					alertManager.showError(localize("_ChangesNotSaved"));

					setDialogContentState({
						kind: "form"
					});
				}

				setIsBusy(false);
			}
		},
		[
			alertManager,
			connectionId,
			dialogContentState.kind,
			formData,
			formHandler.validation,
			globalCancellation,
			isBusy,
			onAfterSave
		]
	);

	const stableOnClose: typeof onClose = useStableImperativeCallback(onClose);

	useEffect(
		(): (() => void) | undefined => {
			if (isNil(connectionId)) {
				return undefined;
			}

			const loadConnection = async (id: number, signal: AbortSignal): Promise<void> => {
				setIsBusy(true);

				try {
					const {
						name,
						domainName,
						clientId
					} = await get(
						id,
						signal
					);

					setFormData({
						name,
						domainName,
						clientId,
						clientSecret: {
							containsNewValue: false,
							value: null
						}
					});

					setIsBusy(false);

				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					stableOnClose();
				}
			};

			const cancellation = new AbortController();
			loadConnection(connectionId, cancellation.signal);
			return (): void => cancellation.abort();
		},
		[
			alertManager,
			connectionId,
			stableOnClose
		]
	);

	const isNewConnection: boolean = connectionId === undefined;

	let titleKey: LocalizationKey;
	let saveKey: LocalizationKey;
	if (isNewConnection) {
		titleKey = "_NewNameConnection";
		saveKey = "_Add";
	} else {
		titleKey = "_EditNameConnection";
		saveKey = "_Save";
	}

	return (
		<Dialog
			hasBusyIndicator={isBusy}
			trackingId="egnyte-connection-dialog"
		>
			<Dialog.Header
				title={
					<FormattedText
						text={localize(titleKey)}
						thingsToReplace={[
							{ what: "{{Name}}", filler: localize("_Egnyte") }
						]}
					/>
				}
				onClose={onClose}
				onBack={dialogContentState.backHandler}
			/>

			{dialogContentState.kind === "form" && (
				<FormStep
					isNewConnection={isNewConnection}
					onSubmit={submitHandler}
					formHandler={formHandler}
					saveKey={saveKey}
					onClose={onClose}
				/>
			)}

			{dialogContentState.kind === "authentication" && (
				<AuthenticationStep
					authenticationUrl={dialogContentState.authenticationUrl}
					onFinish={submitHandler}
					onClose={onClose}
				/>
			)}
		</Dialog>
	);
};
EgnyteConnectionDialog.displayName = "EgnyteConnectionDialog";

type FormStepProps = {
	isNewConnection: boolean;
	onSubmit: () => void;
	formHandler: IFormHandler<EgnyteConnectionSaveModel>;
	saveKey: LocalizationKey;
	onClose: () => void;
};

const FormStep = (props: FormStepProps): ReactElement => {
	const {
		isNewConnection,
		onSubmit,
		formHandler,
		saveKey,
		onClose
	} = props;

	const enforceToSupplyClientSecret = useCallback(
		(): void => {
			const attachable: IValueInputAttachable<SecretValue> = formHandler.for("clientSecret");

			const { containsNewValue } = attachable.value;
			if (containsNewValue) {
				return;
			}

			attachable.reducer
				.setValue({
					containsNewValue: true,
					value: ""
				});

			attachable.validation
				.triggerInputFinished();
		},
		[formHandler]
	);

	return (
		<>
			<Dialog.Body>
				<Form onSubmit={onSubmit}>
					<FormItem label={localize("_Name")} isRequired>
						<TextInput {...formHandler.for("name").attachToTextInput()} />
						<ValidationError {...formHandler.for("name").attachToValidationError()} />
					</FormItem>

					<FormItem label={localize("_DomainName")} isRequired>
						<InputRow>
							<InputLike value="https://" />
							<InputRow.Stretch>
								<TextInput
									{...formHandler.for("domainName")
										.attachToTextInput()
										.withOnChange(enforceToSupplyClientSecret)
									}
								/>
							</InputRow.Stretch>
							<InputLike value=".egnyte.com" />
						</InputRow>
						<ValidationError {...formHandler.for("domainName").attachToValidationError()} />
					</FormItem>

					<FormItem label={localize("_Key")} isRequired>
						<TextInput
							{...formHandler.for("clientId")
								.attachToTextInput()
								.withOnChange(enforceToSupplyClientSecret)
							}
						/>
						<ValidationError {...formHandler.for("clientId").attachToValidationError()} />
					</FormItem>

					<FormItem label={localize("_Secret")} isRequired>
						<SecretValueInput
							{...formHandler.for("clientSecret").attachToSecretValueInput()}
							containsInitialValue={!isNewConnection}
						/>
						<ValidationError {...formHandler.for("clientSecret").attachToValidationError()} />
					</FormItem>

					<FormItem label={localize("_RedirectUrl")}>
						<CopyControl
							value={createUrlRelativeToEnvironment("/Integrations/Egnyte/OAuth2/HandleAuthorityResponse")}
							buttonKind="secondary"
						/>
					</FormItem>
				</Form>
			</Dialog.Body>

			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize(saveKey)}
						onClick={onSubmit}
						trackingId="save"
					/>

					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</>
	);
};
FormStep.displayName = "FormStep";

type AuthenticationStepProps = {
	authenticationUrl: string;
	onFinish: () => void;
	onClose: () => void;
};

const AuthenticationStep = (props: AuthenticationStepProps): ReactElement => {
	const {
		authenticationUrl,
		onFinish,
		onClose
	} = props;

	const [wasAuthenticateClicked, setWasAuthenticateClicked] = useState<boolean>(false);

	const authenticateHandler = useCallback(
		(): void => {
			setWasAuthenticateClicked(true);
			navigateToUriInPopOutWindow(authenticationUrl);
		},
		[authenticationUrl]
	);

	useEffect(
		(): void => {
			setWasAuthenticateClicked(false);
		},
		[authenticationUrl]
	);

	return (
		<>
			<Dialog.Body>
				<p>{localize("_ToCompleteConnectionConfigurationAuthenticationIsNeededExplanation")}</p>

				{!wasAuthenticateClicked && (
					<Toolbar>
						<Toolbar.Button
							kind="primary"
							text={localize("_Authenticate")}
							onClick={authenticateHandler}
							trackingId="authenticate"
						/>
					</Toolbar>
				)}
			</Dialog.Body>

			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_Finish")}
						onClick={onFinish}
						trackingId="finish"
						isDisabled={!wasAuthenticateClicked}
					/>

					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</>
	);
};
AuthenticationStep.displayName = "AuthenticationStep";

type DialogContentState =
	{
		kind: "form";
		backHandler?: undefined;
	}
	|
	{
		kind: "authentication";
		backHandler: () => void;
		authenticationUrl: string;
	};

const validationConfiguration: IValidationConfiguration<EgnyteConnectionSaveModel> =
	createValidationConfiguration<EgnyteConnectionSaveModel>();

validationConfiguration.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

validationConfiguration.forScalar("domainName")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255),
		inline(
			(value): InputValidationResult => {
				const consistsOnlyOfValidCharacters: boolean = value
					.split("")
					.every(
						(c): boolean => {
							return c === "-"
								|| (c >= "0" && c <= "9")
								|| (c >= "A" && c <= "Z")
								|| (c >= "a" && c <= "z");
						}
					);

				if (consistsOnlyOfValidCharacters) {
					return { kind: InputValidationResultKind.Valid };
				}

				return {
					kind: InputValidationResultKind.Invalid,
					errorMessage: localize("_LandingInvalidCharacters")
				};
			}
		)
	);

validationConfiguration.forScalar("clientId")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

validationConfiguration.forObject("clientSecret")
	.when((x) => x.containsNewValue)
	.forScalar("value")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

export type EgnyteConnectionDialogInitialData = Omit<
	EgnyteConnectionDialogProps,
	"onAfterSave" | "onClose"
>;

export function useEgnyteConnectionDialog(): [
	element: ReactElement | null,
	open: (props: EgnyteConnectionDialogInitialData) => Promise<TryResult<EgnyteConnectionListItemModel>>
] {
	return useDataInputDialog(
		(props, onClose, onSave) => (
			<EgnyteConnectionDialog
				{...props}
				onClose={onClose}
				onAfterSave={onSave}
			/>
		)
	);
}
